/**
 * THE CAMERA/INTEREST CONTRACT — how wide a view a player gets, and what the server therefore
 * has to replicate to them.
 *
 * These two numbers used to live in two places and disagreed. The camera capped its widest view
 * at `MAX_VIEW_HALF_HEIGHT = INTEREST_RADIUS`, which reads like a tie-down and is not one: a
 * *half-height* is only half of a viewport, and the corner of a 16:9 screen framed 900 units
 * tall sits 1 836 units from the middle of it — twice as far out as the circle the server was
 * culling to. Everything between the circle and the corner was drawn from whatever the last
 * frame happened to still hold, so entities near the screen edge flickered in and out as the
 * player moved. Measured against a 900-unit interest radius, *every* viewport tested was showing
 * world the server was not replicating:
 *
 *   | viewport                    | half-diagonal | outside the replicated circle |
 *   | 3440x1440 (21:9)            |         2 331 |                       1 431 u |
 *   | 2560x1440 / 1920x1080       |         1 836 |                         936 u |
 *   | 1680x600 (short, wide)      |         2 676 |                       1 776 u |
 *   | 844x390 (phone, landscape)  |         1 550 |                         650 u |
 *   | 390x844 (phone, portrait)   |           991 |                          91 u |
 *
 * So this file owns *both* halves and derives one from the other. The ladder below is the
 * client's framing; `viewHalfExtentsFor`, `interestRadiusForView` and `interestBudgetForView` are
 * what the
 * server replicates for a viewer framed that way. They cannot drift because there is nothing to
 * keep in step: the camera and the interest query call the same functions with the same inputs.
 *
 * WHAT BOUNDS THE COST IS AREA, NOT RADIUS. Entities in a view scale with the *area* of the
 * rectangle, and replication is the bottleneck by 40-70x, so `MAX_VIEW_AREA` — not a radius and
 * not a zoom — is the ceiling every viewer is held to. Three things fall out of stating it that
 * way:
 *
 * - A wide screen is not a wallhack. An ultrawide viewport is capped to the same *area* as a
 *   16:9 one, so it sees a shorter, wider slice of the world rather than more of it.
 * - A client cannot buy information by lying about its aspect. The aspect it reports only
 *   reshapes a fixed area — the entity budget it gets is identical either way.
 * - A small player costs less than they used to. Region and budget both follow the view, and a
 *   fresh player's view is a little over a quarter of the widest one, so they are replicated a
 *   749-unit circle where the old flat rule sent 900 — most of a room, most of the time.
 *
 * Which is why closing the gap turned out to be cheap. Measured end to end on the reference
 * 16:9 screen, replication went from 18.7 to **17.0 µs per viewer-frame** and 124 to 135 bytes:
 * the region a typical viewer needs shrank, and the wider budget skips the nearest-N sort the
 * flat one paid on every frame. An ultrawide window costs a third more than a 16:9 one, which is
 * the honest price of a wider window and is bounded by MAX_VIEW_ASPECT. Full table in
 * `docs/architecture.md`.
 *
 * ---
 *
 * WHAT THE VIEW IS SIZED FROM IS THE CLUSTER'S **EXTENT**, and the ladder now runs all the way to
 * the largest one the simulation allows. Two more reports, and both were one line in here:
 *
 * - "Once the circle gets to a certain size the zoom out is not happening so it's taking up most
 *   the screen." The ladder ended at a radius of 320 and the biggest blob's was 600, so the whole
 *   top half of the growth curve was framed at one fixed width — the biggest legal blob covered
 *   94% of the height of a 16:9 screen and 133% of the short axis of a 32:9 one.
 * - "When circles belonging to the same user split apart and then end up way apart from each
 *   other ... ALL of the user's circles [should be] in view." The band was chosen from
 *   `massToRadius(clusterMass(cells))` — the radius a cluster would have if it were *whole* —
 *   which says nothing about how far apart the pieces of a split one are. It is
 *   `clusterViewRadius(cells)` now, on both sides of the contract, and for a single cell the two
 *   are the same number to the float.
 *
 * THE CEILING IS STILL AN AREA AND IT STILL BINDS. Framing cannot be unbounded — an extent is not
 * bounded by MAX_PLAYER_MASS, and a viewer framing the whole width of the arena is reading a map
 * rather than playing on one. A cluster that reaches past the top rung is framed at the top rung,
 * which is the answer an ultrawide window already got and for the same reason. Nothing anywhere
 * says "this player is too big"; see MAX_VIEW_HALF_HEIGHT for where the wall is and why.
 *
 * WHAT IT COST, measured through the real gateway path on the same box: replication per
 * viewer-frame roughly **doubles** (14.5 -> 25.2 µs at a typical mass mix) for +22% bytes, and
 * essentially all of it is the extended ladder rather than the extent framing (+5% on its own).
 * Most of that is the interest *query* — the radius at the top went from ~1 400 to ~2 400 units,
 * so `queryNeighbors` scans ~2.9x the area — rather than the byte bill. Attribution table, and the
 * capacity figure it moves, in `docs/architecture.md`.
 */

import { massToRadius } from './mass.js';
import {
  MAX_PLAYER_MASS,
  PELLET_COUNT,
  ROOM_MAX_PLAYERS,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from './simulation.js';

// ---------------------------------------------------------------------------
// The band ladder — how wide a view a player of a given size is framed at.
// ---------------------------------------------------------------------------

/**
 * The view has to widen as a player grows, or a big blob fills the screen and there is nothing
 * left to steer by. But it must not widen *continuously*: a zoom that tracks mass pellet by
 * pellet is a view that is always creeping, and a creeping view is one a player fights rather
 * than reads. So the widening is quantised into a handful of **bands** — a blob eats its way
 * through a band's worth of growth at a fixed view, the world visibly gets bigger in one step,
 * and then it is fixed again. Apparent size still rises across the whole range (the bands widen
 * 3.6x while the blob 39x's), which is what makes eating look like growing.
 */

/**
 * The view at STARTING_MASS, and the narrowest band.
 *
 * Tightened from 380 on the player report that raised BASE_SPEED and MAX_PLAYER_MASS: "the circle
 * speed should start out even faster and more zoomed in to begin". 320 is 16% narrower and 71% of
 * the area, which puts a fresh blob at 5.6% of the height of a 16:9 window rather than 4.7% — a
 * step the same size as a rung of the ladder itself, so it is a view a player reads as closer
 * rather than as a different game.
 *
 * It is also the one change here that makes replication *cheaper*: region and budget both follow
 * the view, and a fresh player is now sent a 749-unit circle rather than an 871-unit one.
 */
export const MIN_VIEW_HALF_HEIGHT = 320;

/**
 * The widest half-height any viewer is ever framed at, on any screen.
 *
 * It used to be INTEREST_RADIUS (900) — see the header for why that was the bug rather than the
 * tie-down it looked like — and then 640, back when the ladder stopped at a cluster of radius
 * 320 and a bigger one simply got no more view. 320 was a little over half the radius of the
 * largest legal blob, so the top half of the growth curve was framed at a fixed width: the blob
 * went on getting bigger and the world did not get bigger with it, which is the first half of the
 * report this ceiling was raised for.
 *
 * **IT IS A BOUND ON AREA AND NEVER ON HOW BIG A PLAYER IS**, which is why the raise is stated
 * here rather than as a rung. Where 1 150 comes from: at `MAX_VIEW_ASPECT` the rectangle
 * `MAX_VIEW_AREA` allows is 5 819 world units across, and the default room is 6 000 — so the
 * widest window anybody plays on still frames strictly less than the arena it is playing in. A
 * viewer framing the whole width of the map is reading a map rather than playing on one, and the
 * replication feeding them is a broadcast with extra steps. That is the wall; a cluster spread
 * wider than the wall is framed at the wall, exactly as an ultrawide window is.
 *
 * **AND THE WALL IS WHY THIS DID NOT FOLLOW MAX_PLAYER_MASS UP.** Raising the mass ceiling to
 * 30 000 took the largest blob's radius from 600 to 693, and the ladder's top rung with it; this
 * number could not go with them, because the arithmetic above already spends 5 819 of the arena's
 * 6 000 units and the most it could ever be is ~1 186. So the biggest legal blob is now a larger
 * share of the screen than it was — about 60% of the narrow axis on a 16:9 window against 52%,
 * and 85% of the short axis of a 32:9 one against 74%. That is the honest price of a bigger
 * ceiling, and it is nowhere near the 94% / 133% the ladder extension was written to fix.
 */
export const MAX_VIEW_HALF_HEIGHT = 1150;

/** One rung of the zoom ladder: hold this view until the cluster's radius reaches the next. */
export interface ViewBand {
  /** Cluster radius, in world units, at which this band is entered on the way up. */
  readonly minRadius: number;
  /** Half the world-space height the camera shows while in this band. */
  readonly halfHeight: number;
}

/**
 * The ladder, narrowest first. Thresholds are radii rather than masses because radius is what
 * the screen actually shows — mass quadruples for every doubling of it, so a mass-spaced ladder
 * would put every band a fresh player can reach in the first few seconds of a life. The radius
 * they are read against is the cluster's **extent** (`clusterViewRadius`), never a radius derived
 * from its mass; see `viewHalfExtentsFor`.
 *
 * IT IS TWO LADDERS END TO END, and the join is at 320.
 *
 * - **Below 320 the view falls behind on purpose.** Six rungs over a 18x growth in radius, each
 *   widening the view by ~14.9% — wide enough apart that a step is a moment rather than a twitch,
 *   close enough that no single step is a jolt. The radius rungs are ~1.8x apart and the view
 *   rungs ~1.149x, so the blob takes a visibly larger share of the screen at every step, which is
 *   what makes eating *look* like growing. **Only the view column moved when the opening band was
 *   tightened**: the radius rungs a player is promoted through are the ones they always were, and
 *   the half is one geometric run from MIN_VIEW_HALF_HEIGHT to the join at 640 rather than a
 *   tighter start bolted onto the old numbers, which would have put a 23% jolt at the first step.
 * - **From 320 up the view KEEPS UP as far as it is allowed to.** Four rungs, and they used to
 *   share a ratio with the radius column so that the share of the screen a cluster takes stopped
 *   rising. It cannot quite any more: the top rung has to land on the largest legal blob and the
 *   view ceiling is pinned by the arena's width (see MAX_VIEW_HALF_HEIGHT), so the radius rungs
 *   are ~1.213x apart against the view's ~1.158x and a cluster's share of the narrow axis creeps
 *   from 0.50 at the join to 0.60 at the top. That is still the whole of the fix — past 320 the
 *   old ladder had nothing left to give at all.
 *
 * THE SPACING HAD TO WIDEN FROM ~10.5% TO ~16%, and it is `VIEW_BAND_HYSTERESIS` that decides
 * that rather than taste. The invariant is that a band's release point stays above the band below
 * it, so the *radius* rungs may never be closer than 1 / VIEW_BAND_HYSTERESIS = 1.136 — which the
 * upper half clears at 1.213 and the lower half clears by a mile at ~1.8. The steps are bigger
 * than they were and the tween is what absorbs them: see `approachZoom` in the client's
 * `camera.ts`, which eases every one of them over ZOOM_TWEEN_MS whether it is one rung or four.
 *
 * **The last rung is entered at exactly `massToRadius(MAX_PLAYER_MASS)`**, and it is written that
 * way rather than as a number so that moving the mass ceiling moves the ladder with it — which is
 * exactly what happened when it went to 30 000 and this rung went from 600 to 693. Past it there
 * is nothing above: a cluster spread wider than the largest legal blob is a split one, and it is
 * held at the area ceiling like everything else here.
 */
export const VIEW_BANDS: readonly ViewBand[] = [
  { minRadius: 0, halfHeight: MIN_VIEW_HALF_HEIGHT },
  { minRadius: 30, halfHeight: 368 },
  { minRadius: 55, halfHeight: 422 },
  { minRadius: 100, halfHeight: 485 },
  { minRadius: 180, halfHeight: 557 },
  { minRadius: 320, halfHeight: 640 },
  { minRadius: 388, halfHeight: 741 },
  { minRadius: 471, halfHeight: 858 },
  { minRadius: 571, halfHeight: 994 },
  { minRadius: massToRadius(MAX_PLAYER_MASS), halfHeight: MAX_VIEW_HALF_HEIGHT },
];

/**
 * How far back below its entry threshold a radius must fall before the band is given up.
 *
 * Every band's threshold times this still sits above the band below it, so shrinking walks the
 * ladder down one rung at a time and never skips one — that invariant is what makes the ladder
 * safe to widen later.
 */
export const VIEW_BAND_HYSTERESIS = 0.88;

/** The band a radius belongs to with no history to respect — a fresh life, or a first frame. */
export function bandForRadius(radius: number): number {
  let band = 0;
  while (band + 1 < VIEW_BANDS.length && radius >= VIEW_BANDS[band + 1].minRadius) band += 1;
  return band;
}

/**
 * The band to be in this frame, given the one in force. At most one of the two walks runs: a
 * radius cannot simultaneously be above the next threshold and below this band's hysteresis.
 */
export function stepViewBand(current: number, radius: number): number {
  if (!Number.isFinite(radius)) return clampBand(current);
  let band = clampBand(current);
  while (band + 1 < VIEW_BANDS.length && radius >= VIEW_BANDS[band + 1].minRadius) band += 1;
  while (band > 0 && radius < VIEW_BANDS[band].minRadius * VIEW_BAND_HYSTERESIS) band -= 1;
  return band;
}

/** Half the world-space height a band shows. Out-of-range indices clamp to the ladder's ends. */
export function bandHalfHeight(band: number): number {
  return VIEW_BANDS[clampBand(band)].halfHeight;
}

/**
 * Half the world-space height the camera should show for a cluster of this view radius, with no
 * hysteresis — the ladder as a plain function, which is what makes the framing curve testable
 * independently of the state machine that walks it.
 */
export function viewHalfHeightFor(viewRadius: number): number {
  return bandHalfHeight(bandForRadius(viewRadius));
}

function clampBand(band: number): number {
  if (!Number.isFinite(band) || band < 0) return 0;
  if (band > VIEW_BANDS.length - 1) return VIEW_BANDS.length - 1;
  return Math.floor(band);
}

// ---------------------------------------------------------------------------
// Aspect: the one thing about a viewer's screen the server cannot work out itself.
// ---------------------------------------------------------------------------

/**
 * The screen the ladder is quoted against, and what a viewer who never told us is assumed to
 * have: 16:9, which is every common desktop and laptop.
 */
export const DEFAULT_VIEW_ASPECT = 16 / 9;

/**
 * The range a reported aspect is clamped into: from a tall phone in portrait to a 32:9
 * super-ultrawide. Outside it a client is either wrong or trying something, and either way the
 * clamp is the answer — the area ceiling already means a wider claim buys no extra entities, so
 * this only stops a silly aspect turning the interest query into a sweep of the whole world.
 */
export const MIN_VIEW_ASPECT = 0.4;
export const MAX_VIEW_ASPECT = 3.6;

/** A client-reported aspect, made safe. Anything unusable falls back to the default. */
export function normaliseViewAspect(aspect: number | undefined): number {
  if (aspect === undefined || !Number.isFinite(aspect) || aspect <= 0) return DEFAULT_VIEW_ASPECT;
  if (aspect < MIN_VIEW_ASPECT) return MIN_VIEW_ASPECT;
  if (aspect > MAX_VIEW_ASPECT) return MAX_VIEW_ASPECT;
  return aspect;
}

// ---------------------------------------------------------------------------
// The area ceiling, and the interest region that covers it.
// ---------------------------------------------------------------------------

/**
 * The most world one viewer may ever be shown, in square world units — the widest rung of the
 * ladder on the reference 16:9 screen.
 *
 * This is the capacity dial. Entities in a view are density times *area*, and the per-viewer
 * entity count is what replication costs, so holding every screen shape to one area is what
 * keeps an ultrawide window from costing 2.4x what a 4:3 one does for the same game.
 */
export const MAX_VIEW_AREA = 4 * MAX_VIEW_HALF_HEIGHT * MAX_VIEW_HALF_HEIGHT * DEFAULT_VIEW_ASPECT;

/**
 * The widest half-height a screen of this shape may be framed at: the ladder's ceiling, trimmed
 * so the rectangle it implies still fits inside MAX_VIEW_AREA.
 *
 * A 16:9 screen reaches the whole ladder. Anything wider gives back height for the width it
 * gains — 552 units at 21:9 — and anything taller than 16:9 is bounded by the ladder alone,
 * because a portrait window's rectangle is well inside the area ceiling at every rung.
 */
export function maxViewHalfHeight(aspect?: number): number {
  const safe = normaliseViewAspect(aspect);
  const fromArea = Math.sqrt(MAX_VIEW_AREA / (4 * safe));
  return Math.min(MAX_VIEW_HALF_HEIGHT, fromArea);
}

/**
 * The widest band a client framing a cluster of this view radius may still be *holding*.
 *
 * `viewHalfHeightFor` is the ladder as a plain function; a real camera walks it with hysteresis
 * and keeps the wider band until the radius has fallen VIEW_BAND_HYSTERESIS below the threshold
 * it came up through. So a viewer whose cluster extends r may be framed anywhere up to the band
 * for r / VIEW_BAND_HYSTERESIS, and it is *that* band the server has to replicate for — a server
 * that assumed the plain ladder would under-replicate every player on the way back down.
 */
export function replicatedHalfHeightFor(viewRadius: number): number {
  return bandHalfHeight(bandForRadius(viewRadius / VIEW_BAND_HYSTERESIS));
}

/** Half the world-space width and height a viewer of this mass sees on a screen of this shape. */
export interface ViewHalfExtents {
  readonly halfWidth: number;
  readonly halfHeight: number;
}

/**
 * The widest rectangle a client framing a cluster of this view radius on a screen of this shape
 * may be holding: the band it could be in, trimmed to the area ceiling for that shape.
 *
 * **The camera and the interest query both come through here** — the camera by walking the ladder
 * with `bandHalfExtents`, the gateway by asking for these extents — which is the whole of the
 * tie-down. There is no second copy of the ladder to keep in step with this one, and the client's
 * framing is bounded by the server's assumption at every size and every aspect by construction
 * rather than by agreement.
 *
 * **THE ARGUMENT IS AN EXTENT, NOT A MASS**, and that is this card's other half. It used to be a
 * mass, turned into a radius with `massToRadius` — the radius a cluster would have if it were
 * whole. A split cluster has exactly the mass it had a tick earlier and is spread over several
 * times the width, so a view sized that way framed the middle of a player and left the halves
 * that had been thrown out in front of them off screen. Every caller now passes
 * `clusterViewRadius(cells)`, which for a whole player is `massToRadius(mass)` to the float and
 * for a split one is what it actually takes to see all of them.
 */
export function viewHalfExtentsFor(viewRadius: number, aspect?: number): ViewHalfExtents {
  const safe = normaliseViewAspect(aspect);
  return frameOnNarrowAxis(replicatedHalfHeightFor(viewRadius), safe);
}

/**
 * A band's half-extents on a screen of this shape: the band names the **narrow** axis, the other
 * axis follows from the aspect, and the rectangle is then trimmed to `MAX_VIEW_AREA`.
 *
 * THE BAND NAMES THE NARROW AXIS, WHICH IT DID NOT USED TO. The ladder was quoted purely as a
 * half-*height* and width was whatever the aspect made it, so a tall screen was framed on the
 * axis it has to spare and starved on the axis it does not. Measured on a 360x800 phone against
 * a 600-unit blob: the view was 576 x 1280 and the blob 1200 across — **208% of the visible
 * width**, filling the screen with the player's own body while using 25% of the area a 16:9
 * viewer gets. The complaint it produces is that the camera "won't zoom out", and it is
 * accurate: nothing in the ladder was expressed in the axis that was actually binding.
 *
 * Framing on the narrow axis is the fix, and the reason it is safe is that **for any aspect >= 1
 * the narrow axis IS the height**, so every landscape and desktop screen is framed exactly as it
 * was before, to the last float. Only portrait viewers move. `view.test.ts` pins that.
 *
 * The area trim also *generalises* `maxViewHalfHeight` rather than sitting beside it: at 21:9
 * both give a half-height of 580, so the wide-screen behaviour that function describes is now a
 * consequence of the area ceiling instead of a second rule that could drift from it.
 */
function frameOnNarrowAxis(band: number, safeAspect: number): ViewHalfExtents {
  // The band is the narrow half-extent; the wide axis is the band divided by the aspect when the
  // screen is taller than it is wide, and multiplied by it when it is wider.
  let halfHeight = safeAspect >= 1 ? band : band / safeAspect;
  let halfWidth = halfHeight * safeAspect;

  // Then the same ceiling every shape is held to. A portrait screen framed on its narrow axis
  // asks for ~25% more area than a 16:9 one at the top rung, and this is where it gives it back:
  // the shape is preserved and both extents shrink together, so the trim can never reintroduce
  // the starved axis it exists to fix.
  const area = 4 * halfWidth * halfHeight;
  if (area > MAX_VIEW_AREA) {
    const scale = Math.sqrt(MAX_VIEW_AREA / area);
    halfHeight *= scale;
    halfWidth *= scale;
  }
  return { halfWidth, halfHeight };
}

/**
 * The half-extents to frame a *band index* at on a screen of this shape — the client camera's
 * entry point, so the camera and `viewHalfExtents` cannot be framed differently.
 *
 * The camera walks the ladder with hysteresis and therefore knows a band rather than a mass,
 * which is the only reason this exists separately from `viewHalfExtents`; both come through
 * `frameOnNarrowAxis`, so there is still exactly one description of what a view looks like.
 */
export function bandHalfExtents(band: number, aspect?: number): ViewHalfExtents {
  return frameOnNarrowAxis(bandHalfHeight(band), normaliseViewAspect(aspect));
}

/**
 * How long a view takes to give back the width it no longer needs — the time constant of the
 * server's release, in milliseconds.
 *
 * THIS EXISTS BECAUSE THE CAMERA IS TWEENED AND THE SIMULATION IS NOT. A cluster's extent can
 * collapse in a single tick — two halves finally merge and a 512-unit reach becomes a 320-unit
 * one — and the camera does not follow it in a single tick: `approachZoom` eases toward the new
 * band over ZOOM_TWEEN_MS, so for the better part of a second the client is still framing the
 * wide view while the extent says narrow. A server that narrowed with the extent would spend
 * that second culling entities the player can still see, which is the exact flicker this file
 * exists to have got rid of.
 *
 * So the server holds the wider view and lets it go on a clock. The rule is one-sided on purpose:
 * WIDENING IS INSTANT — a split has to be replicated on the tick it happens, or the halves fly
 * into world that was never sent — and only narrowing is paced.
 *
 * IT IS AN ORDER OF MAGNITUDE SLOWER THAN THE CAMERA (ZOOM_TWEEN_MS / 3 = 150 ms), and the two
 * reasons are both about what the client is doing rather than about taste:
 *
 * - **It has not started yet.** The client renders the world INTERPOLATION_DELAY_MS behind the
 *   frames it has, and those frames are a network leg behind the tick that made them. So for the
 *   first couple of hundred milliseconds the camera is still fully at the wide band while the
 *   server is already letting go, and no time constant *covers* a delay — only a slow one
 *   outlasts it.
 * - **It arrives asymptotically.** The ease never quite lands, where the server's answer is a
 *   rung: the moment the released radius crosses a threshold the region steps down by a whole
 *   band. So the server must hold the rung above until the camera is inside INTEREST_EDGE_MARGIN
 *   of the rung below, which is another three ease constants after the delay.
 *
 * `view.test.ts` walks it tick by tick against a client modelled 250 ms behind — a 100 ms
 * INTERPOLATION_DELAY_MS and a 300 ms round trip — for every drop a cluster can actually make.
 *
 * WHAT IT COSTS is a viewer holding a region one or two rungs wider than they need for a few
 * seconds after a big merge, and only that viewer pays it. A player who splits and merges over
 * and over therefore sits near the widest view their cluster has recently needed, which is
 * bounded by the top of the ladder like everything else here. What the alternative costs is the
 * flicker at the screen edge that this whole file exists to have got rid of, on the one manoeuvre
 * the game is built around.
 */
export const VIEW_RELEASE_TAU_MS = 1500;

/**
 * The view radius to replicate for this tick: never below what the cluster needs now, and never
 * dropping toward it faster than VIEW_RELEASE_TAU_MS.
 *
 * Pure, so the whole rule is one line and one test rather than a piece of gateway state nobody
 * can check. `held` is the value this returned last time — `GatewayConnection.heldViewRadius`,
 * which starts at 0 so a fresh socket takes the cluster's own extent on its first frame.
 */
export function releaseViewRadius(held: number, viewRadius: number, deltaMs: number): number {
  if (!Number.isFinite(held) || held <= viewRadius) return viewRadius;
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) return held;
  return viewRadius + (held - viewRadius) * Math.exp(-deltaMs / VIEW_RELEASE_TAU_MS);
}

/**
 * Slack outside the viewport corner, in world units.
 *
 * The interest region is a circle and the viewport is a rectangle inside it, so the two touch
 * only at the four corners — which is the one place a cull could still be seen. This margin
 * pushes even that off screen, and covers the two ways an entity can be drawn from just outside
 * the frame: the client interpolates the world INTERPOLATION_DELAY_MS behind and predicts its
 * own movement ahead, and a cell whose centre is outside the corner can still have its edge
 * inside it. 96 units is five ticks of BASE_SPEED, or the radius of a mass-576 blob.
 */
export const INTEREST_EDGE_MARGIN = 96;

/**
 * How far the server has to replicate for a viewer framing a cluster of this view radius on a
 * screen of this shape: the half-diagonal of what the camera frames, plus the margin.
 *
 * A circle rather than the rectangle it circumscribes because that is what the simulation's
 * spatial hash answers, and because a rectangle would have to be rotated with nothing (the view
 * is axis-aligned) for no gain a player could see. It costs candidates the budget then drops,
 * never bytes: `INTEREST_MAX_ENTITIES` is what actually goes out.
 */
export function interestRadiusForView(viewRadius: number, aspect?: number): number {
  const { halfWidth, halfHeight } = viewHalfExtentsFor(viewRadius, aspect);
  return Math.hypot(halfWidth, halfHeight) + INTEREST_EDGE_MARGIN;
}

/**
 * Entities a uniform field puts in one square world unit: the pellet field plus a full room of
 * one-cell players. It is the mean, and the field is deliberately not uniform — see the hotspot
 * block in `simulation.ts` — which is what INTEREST_DENSITY_HEADROOM is for.
 *
 * A DENSITY, which is why nothing here has to know a room's world size. A room is free to open
 * at any size between MIN_WORLD_SIZE and MAX_WORLD_SIZE, and `pelletCountFor` scales its field
 * by area precisely so that this number stays true of it — a viewer's interest budget is about
 * how much is packed around them, and that is the one thing a bigger map does not change.
 */
const FIELD_DENSITY = (PELLET_COUNT + ROOM_MAX_PLAYERS) / (WORLD_WIDTH * WORLD_HEIGHT);

/**
 * How far above the mean field density a view is budgeted for.
 *
 * The field is deliberately not uniform — PELLET_HOTSPOT_SHARE of it is drawn into
 * PELLET_HOTSPOT_COUNT centres — so budgeting for the mean leaves a view inside a hotspot short
 * of dots exactly where a player is most likely to be looking.
 *
 * RE-MEASURED WHEN THE OPENING BAND WAS TIGHTENED TO 320, and the whole table went rather than
 * the one row that moved: the figures that used to be here predate both the current PELLET_COUNT
 * and `frameOnNarrowAxis`, so updating a single row would have put two eras of measurement side
 * by side. Three seeds, 5 000 uniformly placed view centres each, counting the pellets inside the
 * rectangle against the `4 * halfWidth * halfHeight * FIELD_DENSITY` the mean predicts:
 *
 *   | view                            | mean |  p50 |  p90 |   p99 |   max | p99/mean |
 *   | 16:9 fresh, at 320              |   71 |   35 |  175 |   228 |   303 |     3.2x |
 *   | 16:9 fresh, as it was at 380    |  100 |   57 |  224 |   297 |   370 |     3.0x |
 *   | 16:9 at the top of the ladder   |  914 |  675 | 1187 | 1 486 | 1 607 |     1.6x |
 *   | portrait phone, fresh at 320    |   86 |   49 |  192 |   254 |   339 |     3.0x |
 *
 * The multiple *rises as the view shrinks*, because a small rectangle can sit wholly inside a
 * hotspot core while a large one averages the map — which is exactly why tightening the opening
 * band moved it from 3.0x to 3.2x, and why the widest view is the cheapest to budget for. 5
 * covers the p99 of every one of those and the *max* of all four, and it is affordable for the
 * reason the player ceiling below exists: what it buys is pellets, pellets
 * replicate event-driven (`UPDATE_RATE_HZ.pellet` is 0), and a pellet costs its bytes once when
 * it enters a view rather than twenty times a second. Measured end to end, raising it from 1.6
 * to 5 made replication *cheaper* — 21.8 to 19.4 µs per viewer-frame — because a budget that
 * fits the candidates skips the nearest-N sort entirely.
 */
export const INTEREST_DENSITY_HEADROOM = 5;

/**
 * The 20 Hz half of the budget: cells of *other players* one viewer may be sent.
 *
 * This is the one part of a view that costs bytes on every tick, so it is the one part that is
 * not allowed to grow with the view. A pellet is sent once and carried forward by the client; a
 * player is re-sent twenty times a second, and a crowd is the only thing in the game that can
 * put a hundred of them in one place. 60 is exactly what the old flat INTEREST_MAX_ENTITIES
 * allowed a viewer to spend on players — players were filled first out of it — so the tick cost
 * of the busiest pile-up in a room is unchanged by this card.
 */
export const INTEREST_MAX_PLAYER_CELLS = 60;

/** Design target for entities replicated to one client per snapshot; also the budget's floor. */
export const INTEREST_TARGET_ENTITIES = 20;

/**
 * Hard cap per snapshot: what the widest view MAX_VIEW_AREA allows, budgeted at the headroom
 * above, and therefore the most any one viewer can cost. A crowded corner still degrades to the
 * nearest N rather than to a stall.
 *
 * It is a much larger number than the flat 60 it replaces, and it is a different kind of number.
 * That 60 was the *whole* budget for every viewer whatever they could see, and it was the tighter
 * of the two culls behind the reported flicker: it was spent 723 units from the viewer at the
 * median spot on the map and 452 inside a hotspot, both of them well inside the screen. What
 * replaces it is a budget that follows the view (`interestBudgetFor`), and the 20 Hz part of it
 * is pinned separately at INTEREST_MAX_PLAYER_CELLS — which is 60, the same 60. So the per-tick
 * cost ceiling has not moved; what has grown is the number of *pellets* a viewer may hold, and
 * a pellet is sent once.
 */
export const INTEREST_MAX_ENTITIES = Math.ceil(
  MAX_VIEW_AREA * FIELD_DENSITY * INTEREST_DENSITY_HEADROOM,
);

/**
 * The default interest radius, for a viewer whose screen shape is unknown and for the
 * simulation's own reference arithmetic: the widest a default-aspect viewer can ever need.
 *
 * Nothing in the replication path should reach for this in preference to `interestRadiusForView`
 * — a viewer is always some cluster on some screen, and both are known by the time a frame is
 * built. It is genuinely the maximum because the ladder *saturates*: `massToRadius(MAX_PLAYER_MASS)`
 * is the last rung's own threshold, so a cluster spread wider than the biggest legal blob asks
 * for the same region and gets it.
 */
export const INTEREST_RADIUS = interestRadiusForView(
  massToRadius(MAX_PLAYER_MASS),
  DEFAULT_VIEW_ASPECT,
);

/**
 * The entity budget for a viewer framing a cluster of this view radius on a screen of this
 * shape: enough to fill what `viewHalfExtentsFor` frames at INTEREST_DENSITY_HEADROOM times the
 * mean field density, clamped
 * into [INTEREST_TARGET_ENTITIES, INTEREST_MAX_ENTITIES].
 *
 * Deriving it rather than fixing it is what makes covering the whole viewport affordable at all:
 * the cost of a view is its area, most players are small most of the time, and a flat budget
 * charged every one of them for the widest view anybody might have.
 */
export function interestBudgetForView(viewRadius: number, aspect?: number): number {
  const { halfWidth, halfHeight } = viewHalfExtentsFor(viewRadius, aspect);
  const wanted = Math.ceil(4 * halfWidth * halfHeight * FIELD_DENSITY * INTEREST_DENSITY_HEADROOM);
  if (wanted < INTEREST_TARGET_ENTITIES) return INTEREST_TARGET_ENTITIES;
  if (wanted > INTEREST_MAX_ENTITIES) return INTEREST_MAX_ENTITIES;
  return wanted;
}
